import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import { NavigationExperienceId } from '@atlassian/navigation-perf-utils';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { logException, setupSentryClient } from '../../services/sentry';
import type { NavErrorBoundaryConfig } from '../../ui/types';

export function useNavBoundaryContext({
	location = 'unknown',
	disableSentry = false,
	disableAnalytics = false,
	onError,
	sentryConfig,
	interactionId,
	tags,
}: NavErrorBoundaryConfig = {}): NavBoundaryContextType {
	const { fireEvent } = useAnalyticsEvents();

	if (fg('navx-1574-enable-sentry-logs')) {
		try {
			setupSentryClient({ sentryConfig });
		} catch (error) {
			// Log failure to initialize Sentry
			fireEvent('operational.initSentry.failed', {
				location,
				reason: error instanceof Error ? error.message : 'unknown',
			});
		}
	}

	const handleError = useCallback(
		(error: Error) => {
			// Send events to sentry
			if (!disableSentry && fg('navx-1574-enable-sentry-logs')) {
				try {
					logException(error, location, tags);
				} catch (err) {
					// Log failure to send to Sentry
					fireEvent('operational.logToSentry.failed', {
						location,
						reason: err instanceof Error ? err.message : 'unknown',
					});
				}
			}

			// Fire Analytics event
			if (!disableAnalytics) {
				fireEvent('operational.errorBoundary.unhandledErrorCaught', {
					location,
					errorMessage: error.message,
					errorStack: error.stack || 'no stack trace',
				});
			}

			// Mark UFO Experience as errored if provided
			if (interactionId) {
				setInteractionError(NavigationExperienceId[interactionId].experienceId, {
					errorMessage: error.message,
					name: error.name,
				});
			}

			// Invoke external onError
			onError?.(error);
		},
		[disableSentry, disableAnalytics, interactionId, onError, location, tags, fireEvent],
	);

	return { handleError };
}

export type NavBoundaryContextType = {
	handleError: (error: Error, errorInfo?: React.ErrorInfo) => void;
};
